import type { FacetEditorialMap } from "./types";

/** Contenu éditorial par matière (ids alignés sur FACET_REGISTRY.material). */
export const MATERIAL_EDITORIAL: FacetEditorialMap = {
  plastic: {
    related: [
      { key: "shape", valueId: "square" },
      { key: "color", valueId: "brown" },
      { key: "gender", valueId: "unisex" },
    ],
    fr: {
      suitsTitle: "Pourquoi choisir une monture en plastique (acétate) ?",
      suits:
        "L'acétate est la matière la plus utilisée en optique : elle permet des couleurs profondes, des épaisseurs variées et un excellent rapport solidité/prix. C'est aussi la matière la plus adaptée aux fortes corrections, car le cercle épais masque l'épaisseur du verre.",
      keyPoints: [
        { title: "Couleurs", body: "Teinte dans la masse : la couleur ne s'écaille pas avec le temps." },
        { title: "Solidité", body: "Résiste bien aux chocs du quotidien ; se déforme légèrement à la chaleur, ce qui permet de la réajuster." },
        { title: "Corrections fortes", body: "Le cercle épais dissimule les bords de verre : idéal au-delà de -4 dioptries." },
      ],
      advice:
        "Ne laissez pas une monture acétate sur le tableau de bord d'une voiture : la chaleur la déforme. En cas de déformation, un réajustage à chaud en boutique la remet en forme gratuitement.",
      faq: [
        { q: "L'acétate est-il plus fragile que le métal ?", a: "Non. Il encaisse mieux les chocs, mais il craint davantage la chaleur prolongée." },
        { q: "Une monture en plastique est-elle lourde ?", a: "Une monture acétate fine pèse à peine plus qu'une monture métal. Ce sont les modèles très épais qui se ressentent en fin de journée." },
        { q: "Peut-on réajuster une monture acétate ?", a: "Oui, à chaud, par un opticien. L'ajustage est gratuit dans toutes nos boutiques Lapaire." },
      ],
    },
    en: {
      suitsTitle: "Why choose a plastic (acetate) frame?",
      suits:
        "Acetate is the most used material in optics: it allows deep colours, varied thicknesses and an excellent strength-to-price ratio. It is also the best material for strong prescriptions, since a thick rim hides the lens edge.",
      keyPoints: [
        { title: "Colours", body: "Colour runs through the material, so it never chips." },
        { title: "Strength", body: "Handles daily knocks well; softens slightly with heat, which allows reshaping." },
        { title: "Strong prescriptions", body: "The thick rim conceals lens edges: ideal beyond -4 dioptres." },
      ],
      advice:
        "Never leave an acetate frame on a car dashboard: heat warps it. If it warps, a heat adjustment in store restores the shape for free.",
      faq: [
        { q: "Is acetate more fragile than metal?", a: "No. It absorbs impact better, but it is more sensitive to prolonged heat." },
        { q: "Are plastic frames heavy?", a: "A thin acetate frame weighs barely more than a metal one. Only very thick models are noticeable by the end of the day." },
        { q: "Can an acetate frame be readjusted?", a: "Yes, with heat, by an optician. Adjustment is free in every Lapaire store." },
      ],
    },
  },

  metal: {
    related: [
      { key: "shape", valueId: "round" },
      { key: "color", valueId: "gold" },
      { key: "size", valueId: "small" },
    ],
    fr: {
      suitsTitle: "Pourquoi choisir une monture en métal ?",
      suits:
        "Le métal permet des montures très fines et très légères, avec des plaquettes réglables qui s'adaptent précisément à chaque racine de nez. C'est la matière la plus discrète : la monture se fait oublier sur le visage.",
      keyPoints: [
        { title: "Légèreté", body: "Souvent 30 à 40 % plus léger qu'un acétate équivalent." },
        { title: "Ajustement", body: "Les plaquettes réglables corrigent un nez plat ou étroit, ce que ne permet pas l'acétate." },
        { title: "Discrétion", body: "Un fil fin encadre le regard sans encombrer le visage." },
      ],
      advice:
        "Les vis des charnières se desserrent avec le temps : faites-les resserrer une fois par an. C'est gratuit en boutique et cela évite la casse d'une branche.",
      faq: [
        { q: "Le métal résiste-t-il à la transpiration ?", a: "Oui, nos montures sont traitées, mais un rinçage régulier à l'eau claire préserve la finition, surtout sur les plaquettes." },
        { q: "Le métal convient-il aux fortes corrections ?", a: "Le cercle fin laisse voir l'épaisseur du verre au-delà de -4 dioptries. Dans ce cas, l'acétate est plus flatteur." },
        { q: "Une monture métal est-elle solide ?", a: "Oui, mais elle se plie plutôt qu'elle ne casse. Nos opticiens la remettent en forme gratuitement." },
      ],
    },
    en: {
      suitsTitle: "Why choose a metal frame?",
      suits:
        "Metal allows very thin, very light frames with adjustable nose pads that fit each nose bridge precisely. It is the most discreet material: the frame disappears on the face.",
      keyPoints: [
        { title: "Lightness", body: "Often 30 to 40% lighter than an equivalent acetate frame." },
        { title: "Fit", body: "Adjustable pads correct a flat or narrow bridge, which acetate cannot." },
        { title: "Discretion", body: "A thin rim frames the eyes without crowding the face." },
      ],
      advice:
        "Hinge screws loosen over time: have them tightened once a year. It is free in store and prevents a broken temple.",
      faq: [
        { q: "Does metal resist perspiration?", a: "Yes, our frames are treated, but rinsing regularly with clear water preserves the finish, especially on the pads." },
        { q: "Is metal suitable for strong prescriptions?", a: "A thin rim exposes lens thickness beyond -4 dioptres. In that case acetate is more flattering." },
        { q: "Are metal frames sturdy?", a: "Yes, but they bend rather than break. Our opticians reshape them for free." },
      ],
    },
  },

  tr90: {
    related: [
      { key: "shape", valueId: "rectangle" },
      { key: "gender", valueId: "unisex" },
      { key: "color", valueId: "black" },
    ],
    fr: {
      suitsTitle: "Pourquoi choisir une monture en TR90 ?",
      suits:
        "Le TR90 est un polymère souple et ultra-léger. Il reprend sa forme après une torsion, ce qui en fait la matière la plus résistante pour un usage intensif : travail en extérieur, déplacements, enfants et adolescents.",
      keyPoints: [
        { title: "Flexibilité", body: "Se tord sans casser et revient à sa forme initiale." },
        { title: "Poids", body: "Parmi les matières les plus légères du marché : idéale pour un port de 10 h par jour." },
        { title: "Climat", body: "Insensible à la transpiration et à l'humidité, contrairement à certains métaux." },
      ],
      advice:
        "Le TR90 se nettoie à l'eau tiède savonneuse. Évitez les produits alcoolisés, qui ternissent la finition mate au fil des mois.",
      faq: [
        { q: "Le TR90 est-il incassable ?", a: "Pas incassable, mais nettement plus résistant à la torsion et aux chocs que l'acétate ou le métal." },
        { q: "Le TR90 convient-il aux enfants ?", a: "Oui, c'est même la matière la plus recommandée pour les jeunes porteurs et les métiers physiques." },
        { q: "Peut-on ajuster une monture TR90 ?", a: "Partiellement : sa souplesse limite l'ajustage à chaud. En revanche elle s'adapte naturellement à la plupart des visages." },
      ],
    },
    en: {
      suitsTitle: "Why choose a TR90 frame?",
      suits:
        "TR90 is a flexible, ultra-light polymer. It springs back after being twisted, making it the toughest material for intensive use: outdoor work, travel, children and teenagers.",
      keyPoints: [
        { title: "Flexibility", body: "Twists without breaking and returns to its original shape." },
        { title: "Weight", body: "Among the lightest materials available: ideal for 10 hours of wear a day." },
        { title: "Climate", body: "Unaffected by perspiration and humidity, unlike some metals." },
      ],
      advice:
        "Clean TR90 with lukewarm soapy water. Avoid alcohol-based products, which dull the matte finish over months.",
      faq: [
        { q: "Is TR90 unbreakable?", a: "Not unbreakable, but clearly more resistant to twisting and impact than acetate or metal." },
        { q: "Is TR90 suitable for children?", a: "Yes, it is the most recommended material for young wearers and physical jobs." },
        { q: "Can a TR90 frame be adjusted?", a: "Partly: its flexibility limits heat adjustment, but it naturally conforms to most faces." },
      ],
    },
  },

  mixed: {
    related: [
      { key: "shape", valueId: "browline" },
      { key: "color", valueId: "brown" },
      { key: "gender", valueId: "men" },
    ],
    fr: {
      suitsTitle: "Pourquoi choisir une monture mixte (acétate + métal) ?",
      suits:
        "Les montures mixtes combinent le meilleur des deux matières : la profondeur de couleur de l'acétate sur le haut, la finesse et la légèreté du métal sur le bas ou sur les branches. C'est la construction typique des browline.",
      keyPoints: [
        { title: "Équilibre", body: "Du caractère en haut, de la légèreté en bas." },
        { title: "Ajustement", body: "Branches métal réglables, donc un maintien plus précis derrière l'oreille." },
        { title: "Style", body: "Rendu habillé sans l'épaisseur d'un acétate intégral." },
      ],
      advice:
        "Sur une monture mixte, contrôlez une fois par an la jonction entre les deux matières : c'est le point qui bouge en premier après plusieurs mois de port.",
      faq: [
        { q: "Une monture mixte est-elle plus fragile ?", a: "Non, à condition de ne pas la tordre au niveau de la jonction. Les réparations restent gratuites dans nos boutiques." },
        { q: "Les montures mixtes sont-elles unisexes ?", a: "En grande majorité oui : c'est l'une des constructions les plus polyvalentes du catalogue." },
      ],
    },
    en: {
      suitsTitle: "Why choose a mixed frame (acetate + metal)?",
      suits:
        "Mixed frames combine the best of both materials: acetate colour depth on top, metal thinness and lightness below or on the temples. This is the typical browline construction.",
      keyPoints: [
        { title: "Balance", body: "Character on top, lightness below." },
        { title: "Fit", body: "Adjustable metal temples give a more precise hold behind the ear." },
        { title: "Style", body: "A dressed-up look without the bulk of full acetate." },
      ],
      advice:
        "On a mixed frame, check the junction between the two materials once a year: it is the first point to move after months of wear.",
      faq: [
        { q: "Are mixed frames more fragile?", a: "No, as long as you avoid twisting at the junction. Repairs stay free in our stores." },
        { q: "Are mixed frames unisex?", a: "Mostly yes: it is one of the most versatile constructions in the catalogue." },
      ],
    },
  },

  acetate: {
    related: [
      { key: "shape", valueId: "square" },
      { key: "color", valueId: "brown" },
      { key: "gender", valueId: "unisex" },
    ],
    fr: {
      suitsTitle: "Pourquoi choisir une monture en acétate ?",
      suits: "L'acétate de cellulose est le matériau noble de l'optique. Plus riche que le simple plastique, il offre des finitions brillantes, des dégradés de couleurs uniques et une sensation soyeuse au toucher.",
      keyPoints: [
        { title: "Esthétique", body: "Permet des jeux de transparence et des écailles impossibles avec d'autres matières." },
        { title: "Confort", body: "Une matière 'chaude' qui s'adapte à la température de la peau." },
        { title: "Qualité", body: "Matériau résistant qui garde son éclat après polissage." },
      ],
      advice: "Nettoyez vos lunettes en acétate à l'eau froide. L'eau trop chaude peut altérer la brillance de la matière à long terme.",
      faq: [
        { q: "Quelle est la différence entre plastique et acétate ?", a: "L'acétate est une matière d'origine végétale (fleur de coton) plus résistante et offrant des couleurs plus profondes que le plastique injecté." },
      ],
    },
    en: {
      suitsTitle: "Why choose an acetate frame?",
      suits: "Cellulose acetate is the premium material of optics. Richer than basic plastic, it offers glossy finishes, unique colour gradients and a silky feel to the touch.",
      keyPoints: [
        { title: "Aesthetics", body: "Allows for transparency and tortoiseshell patterns impossible with other materials." },
        { title: "Comfort", body: "A 'warm' material that adapts to skin temperature." },
        { title: "Quality", body: "Durable material that keeps its shine after polishing." },
      ],
      advice: "Clean your acetate glasses with cold water. Very hot water can dull the material's shine over time.",
      faq: [
        { q: "What is the difference between plastic and acetate?", a: "Acetate is a plant-based material (cotton flower) that is stronger and offers deeper colours than injected plastic." },
      ],
    },
  },

  titanium: {
    related: [
      { key: "shape", valueId: "round" },
      { key: "color", valueId: "grey" },
      { key: "size", valueId: "small" },
    ],
    fr: {
      suitsTitle: "Pourquoi choisir une monture en titane ?",
      suits: "Le titane est le matériau ultime pour les lunettes : ultra-léger, biocompatible et pratiquement indestructible. C'est le choix de ceux qui recherchent l'excellence technique.",
      keyPoints: [
        { title: "Légèreté extrême", body: "Jusqu'à 50% plus léger que l'acier, on finit par oublier qu'on porte des lunettes." },
        { title: "Hypoallergénique", body: "Totalement biocompatible, idéal pour les peaux très sensibles." },
        { title: "Robustesse", body: "Résiste à la corrosion, à l'eau de mer et à la transpiration sans jamais s'altérer." },
      ],
      advice: "Une monture en titane est un investissement sur le long terme : elle gardera sa forme et son aspect pendant de nombreuses années.",
      faq: [
        { q: "Le titane est-il incassable ?", a: "Il est extrêmement résistant et possède une mémoire de forme, ce qui le rend bien plus robuste que n'importe quel autre métal." },
      ],
    },
    en: {
      suitsTitle: "Why choose a titanium frame?",
      suits: "Titanium is the ultimate material for eyewear: ultra-light, biocompatible and virtually indestructible. It's the choice for those seeking technical excellence.",
      keyPoints: [
        { title: "Extreme lightness", body: "Up to 50% lighter than steel, you'll eventually forget you're wearing glasses." },
        { title: "Hypoallergenic", body: "Fully biocompatible, ideal for very sensitive skin." },
        { title: "Robustness", body: "Resists corrosion, seawater and perspiration without ever altering." },
      ],
      advice: "A titanium frame is a long-term investment: it will keep its shape and appearance for many years.",
      faq: [
        { q: "Is titanium unbreakable?", a: "It is extremely resistant and has shape memory, making it much more robust than any other metal." },
      ],
    },
  },
};
